import React from 'react';
import { useAuthQuery as useQuery } from '../../../../../shared/hooks/useAuthQuery';
import { api } from '../../../../../convex/_generated/api';
import { Spinner } from '../../../../../catalyst-components/spinner';
import { DailyStatsChart } from '../../../../../shared/components/DailyStatsChart';
import { HorizontalBarChart } from './HorizontalBarChart';
import { CohortRetentionGrid } from './CohortRetentionGrid';

// Convert ISO week to date string
function isoWeekToDate(isoWeek: string): string {
    const [yearStr, weekStr] = isoWeek.split('-W');
    const year = parseInt(yearStr);
    const week = parseInt(weekStr);
    const jan4 = new Date(year, 0, 4);
    const dayOfWeek = jan4.getDay() || 7;
    const monday = new Date(jan4);
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
    return monday.toISOString().split('T')[0];
}

export const RetentionTab: React.FC = () => {
    const data = useQuery(api.admin_engagement_analytics.getRetentionAndEconomyStats);

    if (!data) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spinner className="h-12 w-12" />
            </div>
        );
    }

    const streakData = Object.entries(data.streakDistribution).map(([label, value]) => ({ label: `${label} days`, value }));

    const earnedChartData = data.acornEconomy.map(d => ({
        date: isoWeekToDate(d.week),
        count: d.earned,
    }));

    const spentChartData = data.acornEconomy.map(d => ({
        date: isoWeekToDate(d.week),
        count: d.spent,
    }));

    const totalEarned = data.acornEconomy.reduce((sum, d) => sum + d.earned, 0);
    const totalSpent = data.acornEconomy.reduce((sum, d) => sum + d.spent, 0);

    return (
        <div className="space-y-8">
            {/* Cohort Retention Grid */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Cohort Retention</h3>
                <p className="text-sm text-muted-foreground">
                    Shows what % of users from each signup week are still active N weeks later.
                    Based on last login date (approximation — only captures most recent login).
                </p>
                <CohortRetentionGrid
                    data={data.cohortRetention}
                    title="Weekly Cohort Retention"
                    subtitle="% of cohort with last login at or after week N"
                />
            </div>

            {/* Login Streak Distribution */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Login Streak Distribution</h3>
                <p className="text-sm text-muted-foreground">
                    Current snapshot of consecutive login day streaks. Higher streaks indicate better retention.
                </p>
                <HorizontalBarChart
                    data={streakData}
                    title="Login Streak Buckets"
                    sortBy="label"
                />
            </div>

            {/* Acorn Economy */}
            <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground">Acorn Economy</h3>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Total Earned: </span>
                        <span className="font-semibold text-green-600">{totalEarned}</span>
                    </div>
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Total Spent: </span>
                        <span className="font-semibold text-amber-600">{totalSpent}</span>
                    </div>
                    <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                        <span className="text-sm text-muted-foreground">Earned/Spent Ratio: </span>
                        <span className="font-semibold text-foreground">
                            {totalSpent > 0 ? (totalEarned / totalSpent).toFixed(1) : totalEarned > 0 ? '∞' : '0'}
                        </span>
                    </div>
                </div>
                {earnedChartData.length > 0 && (
                    <>
                        <DailyStatsChart
                            data={earnedChartData}
                            title="Weekly Acorns Earned"
                            lineColor="#10b981"
                            yAxisLabel="Acorns"
                            tooltipSuffix="earned"
                        />
                        <DailyStatsChart
                            data={spentChartData}
                            title="Weekly Acorns Spent"
                            lineColor="#f59e0b"
                            yAxisLabel="Acorns"
                            tooltipSuffix="spent"
                        />
                    </>
                )}
            </div>
        </div>
    );
};
